import { normalizeBaseUrl, type UserProfile } from '@skychat/shared-core';

export interface BotCommandDefinition {
  key: string;
  label?: string;
  hint?: string;
}

export interface BotDefinition {
  id: string;
  mention: string;
  name: string;
  avatarUrl?: string;
  description?: string;
  commands?: BotCommandDefinition[];
}

const SOCKET_PRESENCE_VALUES = new Set([
  'available', 'online', 'working', 'studying', 'gaming', 'custom', 'busy', 'away', 'dnd', 'offline'
]);

export function normalizeBotList(value: unknown): BotDefinition[] {
  const raw = Array.isArray(value)
    ? value
    : value && typeof value === 'object' && Array.isArray((value as { bots?: unknown }).bots)
      ? (value as { bots: unknown[] }).bots
      : [];
  return raw
    .map((item) => item && typeof item === 'object' ? item as Record<string, unknown> : null)
    .filter((item): item is Record<string, unknown> => !!item)
    .map((item) => {
      const commands: BotCommandDefinition[] = Array.isArray(item.commands)
        ? item.commands.reduce<BotCommandDefinition[]>((acc, command) => {
          if (!command || typeof command !== 'object') return acc;
          const rawCommand = command as Record<string, unknown>;
          const key = String(rawCommand.key || '').trim();
          if (!key) return acc;
          acc.push({
            key,
            label: String(rawCommand.label || rawCommand.key || '').trim(),
            hint: String(rawCommand.hint || '')
          });
          return acc;
        }, [])
        : [];
      return {
        id: String(item.id || '').trim(),
        mention: String(item.mention || '').trim(),
        name: String(item.name || item.id || '').trim(),
        avatarUrl: String(item.avatarUrl || item.avatar_url || ''),
        description: String(item.description || ''),
        commands
      };
    })
    .filter((bot) => bot.id && bot.mention);
}

function stringField(payload: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(payload, key)) return String(payload[key] || '');
  }
  return undefined;
}

function boolField(payload: Record<string, unknown>, ...keys: string[]): boolean | undefined {
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(payload, key)) continue;
    const value = payload[key];
    return value === true || value === 1 || value === '1' || value === 'true';
  }
  return undefined;
}

export function normalizeConfiguredPresence(value: unknown, fallback = 'available'): string {
  const normalized = String(value || '').toLowerCase().trim();
  if (!SOCKET_PRESENCE_VALUES.has(normalized) || normalized === 'offline' || normalized === 'online') {
    const normalizedFallback = String(fallback || 'available').toLowerCase().trim();
    return SOCKET_PRESENCE_VALUES.has(normalizedFallback) && !['offline', 'online'].includes(normalizedFallback)
      ? normalizedFallback
      : 'available';
  }
  return normalized;
}

export function preferredPresenceStorageKey(apiBaseUrl: string, userId: string): string {
  return `skychat_preferred_presence:${normalizeBaseUrl(apiBaseUrl)}:${userId}`;
}

export function readPreferredPresence(
  apiBaseUrl: string,
  userId: string,
  storage: Pick<Storage, 'getItem'> | undefined = globalThis.localStorage
): string {
  if (!apiBaseUrl || !userId || !storage) return '';
  try {
    const stored = storage.getItem(preferredPresenceStorageKey(apiBaseUrl, userId));
    return stored ? normalizeConfiguredPresence(stored) : '';
  } catch {
    return '';
  }
}

export function writePreferredPresence(
  apiBaseUrl: string,
  userId: string,
  presence: unknown,
  storage: Pick<Storage, 'setItem'> | undefined = globalThis.localStorage
): void {
  if (!apiBaseUrl || !userId || !storage) return;
  try {
    storage.setItem(preferredPresenceStorageKey(apiBaseUrl, userId), normalizeConfiguredPresence(presence));
  } catch {
    // In-memory state remains authoritative when private browsing blocks storage.
  }
}

export function socketPresenceRevision(payload: Record<string, unknown>, now = () => Date.now()): number {
  const values = [
    payload.presenceRevision, payload.presence_revision, payload.revision, payload.version,
    payload.updatedAt, payload.updated_at, payload.serverTime, payload.server_time,
    payload.ts, payload.timestamp
  ];
  for (const value of values) {
    const numeric = Number(value || 0);
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
  }
  return now();
}

export function presencePatchFromSocketPayload(
  payload: Record<string, unknown>,
  fallbackPresence = 'available'
): Partial<UserProfile> {
  const hasPresence = ['presence', 'status', 'availability']
    .some((key) => Object.prototype.hasOwnProperty.call(payload, key));
  const rawPresence = String(payload.presence || payload.status || payload.availability || '').toLowerCase().trim();
  const hasOnline = ['online', 'isOnline', 'connected']
    .some((key) => Object.prototype.hasOwnProperty.call(payload, key));
  const online = payload.online === true || payload.isOnline === true || payload.connected === true;
  const fallback = normalizeConfiguredPresence(fallbackPresence);
  const patch: Partial<UserProfile> = {};
  const mutablePatch = patch as Record<string, unknown>;

  if (hasPresence) {
    mutablePatch.presence = rawPresence === 'online'
      ? 'available'
      : rawPresence === 'offline'
        ? fallback
        : normalizeConfiguredPresence(rawPresence, fallback);
  }
  if (hasOnline) mutablePatch.online = online;

  const customIcon = stringField(payload, 'customIcon', 'custom_status_icon', 'customStatusIcon', 'status_icon', 'statusIcon');
  const customText = stringField(payload, 'customText', 'custom_status_text', 'customStatusText', 'status_text', 'statusText');
  const customColor = stringField(payload, 'customColorHex', 'custom_status_color_hex', 'customStatusColorHex', 'status_color_hex', 'statusColorHex');
  const customActive = boolField(payload, 'customStatusActive', 'custom_status_active', 'customStatusEnabled', 'status_active', 'statusActive');
  const effectivePresence = String(mutablePatch.presence || fallback);
  const active = customActive ?? (effectivePresence === 'custom' ? !!(customIcon && customText) : undefined);
  if (customIcon !== undefined) mutablePatch.custom_status_icon = customIcon;
  if (customText !== undefined) mutablePatch.custom_status_text = customText;
  if (customColor !== undefined) mutablePatch.custom_status_color_hex = customColor || null;
  if (active !== undefined) mutablePatch.custom_status_active = active;
  if (active === false && effectivePresence !== 'custom') {
    mutablePatch.custom_status_icon = null;
    mutablePatch.custom_status_text = null;
  }
  if (Object.prototype.hasOwnProperty.call(payload, 'custom_status_auto_reply_enabled')) {
    mutablePatch.custom_status_auto_reply_enabled = boolField(payload, 'custom_status_auto_reply_enabled');
  }
  if (Object.prototype.hasOwnProperty.call(payload, 'custom_status_schedule_mode')) {
    mutablePatch.custom_status_schedule_mode = payload.custom_status_schedule_mode || 'none';
  }
  if (Object.prototype.hasOwnProperty.call(payload, 'custom_status_expires_at')) {
    mutablePatch.custom_status_expires_at = payload.custom_status_expires_at || null;
  }
  if (Object.prototype.hasOwnProperty.call(payload, 'custom_status_countdown_started_at')) {
    mutablePatch.custom_status_countdown_started_at = payload.custom_status_countdown_started_at || null;
  }
  if (Object.prototype.hasOwnProperty.call(payload, 'custom_status_countdown_remaining_ms')) {
    mutablePatch.custom_status_countdown_remaining_ms = payload.custom_status_countdown_remaining_ms ?? null;
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'currentGame')) mutablePatch.currentGame = payload.currentGame || null;
  if (Object.prototype.hasOwnProperty.call(payload, 'currentGameSessionId')) mutablePatch.currentGameSessionId = payload.currentGameSessionId || null;
  if (Object.prototype.hasOwnProperty.call(payload, 'last_seen')) mutablePatch.last_seen = payload.last_seen;

  if (hasOnline && !online) {
    mutablePatch.connectedDevices = [];
    mutablePatch.connectedDeviceKinds = [];
    mutablePatch.hasConnectedDevice = false;
    mutablePatch.primaryDeviceKind = null;
  } else {
    if (Array.isArray(payload.connectedDevices)) mutablePatch.connectedDevices = payload.connectedDevices;
    if (Array.isArray(payload.connectedDeviceKinds)) mutablePatch.connectedDeviceKinds = payload.connectedDeviceKinds;
    if (Object.prototype.hasOwnProperty.call(payload, 'hasConnectedDevice')) mutablePatch.hasConnectedDevice = !!payload.hasConnectedDevice;
    if (Object.prototype.hasOwnProperty.call(payload, 'primaryDeviceKind')) mutablePatch.primaryDeviceKind = payload.primaryDeviceKind || null;
  }
  if (Object.prototype.hasOwnProperty.call(payload, 'lastDeviceKind')) mutablePatch.lastDeviceKind = payload.lastDeviceKind || 'unknown';
  if (Object.prototype.hasOwnProperty.call(payload, 'lastDeviceSeenAt')) mutablePatch.lastDeviceSeenAt = payload.lastDeviceSeenAt || 0;
  return patch;
}

export function hasPresencePayload(payload: Record<string, unknown>): boolean {
  return [
    'presence', 'status', 'availability', 'online', 'isOnline', 'connected',
    'customIcon', 'custom_status_icon', 'customStatusIcon',
    'customText', 'custom_status_text', 'customStatusText',
    'customColorHex', 'custom_status_color_hex', 'customStatusColorHex',
    'customStatusActive', 'custom_status_active'
  ].some((key) => Object.prototype.hasOwnProperty.call(payload, key));
}
